import { Button } from "@tokenterminal/core/ui/button/Button"
import { Modal } from "@tokenterminal/core/ui/overlays/modal/Modal"
import { twMerge } from "tailwind-merge"
import { ScrollFade } from "../../../ui/layout/scroll-fade/ScrollFade"
import type { TooltipExpandedData } from "./types"

type ItemRowProps = {
  seriesName: string
  color?: string
  value: number
  formatter?: (value: number) => string
  isTotal?: boolean
  className?: string
  seriesNameClassName?: string
}

function ItemRow({
  seriesName,
  color,
  value,
  formatter,
  className,
  seriesNameClassName,
}: ItemRowProps) {
  const formattedValue = formatter ? formatter(value) : String(value)

  return (
    <div
      className={twMerge(
        "flex items-center justify-between gap-4 px-3 py-2 border-b border-solid border-border-default last:border-b-0",
        className
      )}
    >
      <div className="flex items-center gap-2 min-w-0">
        {color && (
          <span
            className="w-2.5 h-2.5 rounded-sm shrink-0"
            style={{ background: color }}
          />
        )}
        <div
          className={twMerge("truncate", seriesNameClassName)}
          title={seriesName}
        >
          {seriesName}
        </div>
      </div>
      <div
        className="text-right whitespace-nowrap shrink-0"
        style={{ fontFamily: "var(--font-geist-mono)" }}
      >
        {formattedValue}
      </div>
    </div>
  )
}

type ExpandedTooltipModalProps = {
  isOpen: boolean
  onClose: () => void
  data: TooltipExpandedData | null
}

export function ExpandedTooltipModal({
  isOpen,
  onClose,
  data,
}: ExpandedTooltipModalProps) {
  if (!data) return null

  const showTotal = data.showTotal && data.points.length > 1

  return (
    <Modal isOpen={isOpen} onOpenChange={onClose}>
      <Modal.Header title={data.header} subtitle={data.subheader} />
      <Modal.Body>
        <div className="flex-1 min-h-0 flex flex-col gap-2">
          {/* <div className="flex items-center justify-between gap-4 mb-2 px-0 text-fg-secondary text-sm">
              <div>Series</div>
              <div>Value</div>
            </div> */}
          {showTotal && data.total !== undefined && (
            <ItemRow
              seriesName={data.totalLabel ?? "Total"}
              value={data.total}
              formatter={data.totalFormatter ?? data.points[0]?.formatter}
              className="shrink-0 bg-neutral-50 dark:bg-neutral-900 border last:border-b border-solid border-border-default rounded-lg overflow-hidden"
              seriesNameClassName="font-medium"
            />
          )}
          <ScrollFade
            axis="y"
            className="w-full flex-1 flex flex-col border border-solid border-border-default rounded-lg max-h-[500px] overflow-y-auto"
          >
            {data.points.map((point, i) => (
              <ItemRow
                key={i}
                seriesName={point.seriesName}
                color={point.color}
                value={point.y}
                formatter={point.formatter}
              />
            ))}
          </ScrollFade>
        </div>
      </Modal.Body>
      <Modal.Footer className="justify-end">
        <Button onPress={onClose}>Close</Button>
      </Modal.Footer>
    </Modal>
  )
}
